import { useNavigate } from "react-router-dom";

import { ErrorComponent } from "@components/ErrorComponent";
import { ListItem } from "@components/ListItem";
import { Loader } from "@components/Loader";
import { MainLayout } from "@components/MainLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { AccountProvider, DocumentType, DocumentTypeInfo, Routes } from "@ctypes";
import { useDocumentTypes, useUser } from "@hooks";
import { i18n, Translations } from "@i18n";
import { useDocumentsRouteStyles } from "./styles";
import { DocumentsListRow, DocumentTypeConfig } from "./types";

const DOCUMENT_TYPE_CONFIGS: DocumentTypeConfig[] = [
  {
    type: DocumentType.AccountStatement,
    titleKey: Translations.DocumentsRouteAccountStatementsTitle,
    subtitleKey: Translations.DocumentsRouteAccountStatementsSubtitle,
  },
  {
    type: DocumentType.TradeConfirmation,
    titleKey: Translations.DocumentsRouteTradeConfirmationsTitle,
    subtitleKey: Translations.DocumentsRouteTradeConfirmationsSubtitle,
  },
  {
    type: DocumentType.Tax,
    titleKey: Translations.DocumentsRouteTaxDocumentsTitle,
    subtitleKey: Translations.DocumentsRouteTaxDocumentsSubtitle,
  },
];

export const DocumentsRoute = () => {
  const navigate = useNavigate();
  const styles = useDocumentsRouteStyles();
  const { user } = useUser();
  const { documentTypes, loading, error } = useDocumentTypes();

  const showAgreementsAndDisclosures = user?.provider === AccountProvider.Alpaca;

  const handleAgreementsAndDisclosures = () => {
    navigate(Routes.AlpacaAccountDocuments);
  };

  if (loading) {
    return (
      <MainLayout showTab pageHeader={i18n.t(Translations.DocumentsRouteHeader)}>
        <Loader />
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout showTab pageHeader={i18n.t(Translations.DocumentsRouteHeader)}>
        <ErrorComponent />
      </MainLayout>
    );
  }

  const listRows: DocumentsListRow[] = DOCUMENT_TYPE_CONFIGS.map((config) => ({
    kind: "document",
    config,
  }));

  if (showAgreementsAndDisclosures) {
    listRows.push({ kind: "agreements" });
  }

  const rowTitle = (row: DocumentsListRow) =>
    row.kind === "document"
      ? i18n.t(row.config.titleKey)
      : i18n.t(Translations.DocumentsRouteAgreementsAndDisclosuresTitle);

  listRows.sort((a, b) => rowTitle(a).localeCompare(rowTitle(b)));

  return (
    <MainLayout showTab pageHeader={i18n.t(Translations.DocumentsRouteHeader)}>
      <Spacing size={SpacingSizes.XL} />
      {listRows.map((row) => {
        if (row.kind === "agreements") {
          return (
            <div key="agreements-and-disclosures">
              <ListItem
                title={i18n.t(Translations.DocumentsRouteAgreementsAndDisclosuresTitle)}
                subtitle={i18n.t(Translations.DocumentsRouteAgreementsAndDisclosuresSubtitle)}
                onClick={handleAgreementsAndDisclosures}
              />
              <Spacing size={SpacingSizes.S} />
            </div>
          );
        }

        const { config } = row;
        const apiData = documentTypes?.find((dt: DocumentTypeInfo) => dt.type === config.type);
        const unreadCount = apiData?.unreadCount ?? 0;
        const titleIcon = unreadCount > 0 ? <span style={styles.unreadDot} /> : undefined;

        return (
          <div key={config.type}>
            <ListItem
              title={i18n.t(config.titleKey)}
              subtitle={i18n.t(config.subtitleKey)}
              titleIcon={titleIcon}
              onClick={() =>
                navigate(Routes.DocumentCategory, { state: {
                  type: config.type,
                  title: i18n.t(config.titleKey),
                } })
              }
            />
            <Spacing size={SpacingSizes.S} />
          </div>
        );
      })}
    </MainLayout>
  );
};
